import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiDroplet,
  FiActivity,
  FiLayers,
  FiLink,
  FiGrid,
  FiSliders,
  FiList,
  FiCheckSquare,
  FiFileText,
  FiTable,
  FiCopy,
  FiTrendingDown,
} from 'react-icons/fi';
import { Sidebar } from '../sidebar';
import { SidebarHeader } from './SidebarHeader';
import type { ProjectOption } from './SidebarHeader';
import { UserInfo, UserInfoProps } from './UserInfo';
import styles from './PageSidebar.module.css';

type IconProps = { size?: number; color?: string };
const DropletIcon = FiDroplet as React.FC<IconProps>;
const ActivityIcon = FiActivity as React.FC<IconProps>;
const LayersIcon = FiLayers as React.FC<IconProps>;
const LinkIcon = FiLink as React.FC<IconProps>;
const GridIcon = FiGrid as React.FC<IconProps>;
const SlidersIcon = FiSliders as React.FC<IconProps>;
const ListIcon = FiList as React.FC<IconProps>;
const CheckSquareIcon = FiCheckSquare as React.FC<IconProps>;
const FileTextIcon = FiFileText as React.FC<IconProps>;
const TableIcon = FiTable as React.FC<IconProps>;
const CopyIcon = FiCopy as React.FC<IconProps>;
const TrendingDownIcon = FiTrendingDown as React.FC<IconProps>;

export interface PageSidebarNavItem {
  id: string;
  label: React.ReactNode;
  icon: React.ReactNode;
  path?: string;
  active?: boolean;
  onClick?: () => void;
  badge?: React.ReactNode;
  badgeTooltip?: string;
  disabled?: boolean;
}

export interface PageSidebarSection {
  label: string;
  items: PageSidebarNavItem[];
}

export interface PageSidebarProps extends Omit<React.ComponentPropsWithoutRef<'aside'>, 'children'> {
  projectName?: string;
  projectSubtitle?: string;
  onBackToProjects?: () => void;
  projects?: ProjectOption[];
  onNewProject?: () => void;
  onImportProject?: () => void;
  onProjectSettings?: () => void;
  sections?: PageSidebarSection[];
  user?: UserInfoProps;
  userAction?: React.ReactNode;
  footer?: React.ReactNode;
}

const DEFAULT_SECTIONS: PageSidebarSection[] = [
  {
    label: 'SCAL Bridge',
    items: [
      {
        id: 'capillary-pressure',
        label: 'Capillary Pressure Analysis',
        icon: <DropletIcon size={15} />,
        path: '/capillary-pressure',
        badge: 'Samples',
        badgeTooltip: 'This page is used for sample data manipulation',
      },
      {
        id: 'kr-analysis',
        label: 'Kr Analysis',
        icon: <ActivityIcon size={15} />,
        path: '/kr-analysis',
        badge: 'Samples',
        badgeTooltip: 'This page is used for sample data manipulation',
      },
      {
        id: 'multi-analysis',
        label: (
          <span className={styles.itemLabelWithTag}>
            Multi Analysis
            <span className={styles.betaTag}>beta</span>
          </span>
        ),
        icon: <CopyIcon size={15} />,
        path: '/multi-analysis',
        badge: 'Samples',
        badgeTooltip: 'Under development — batch analysis across multiple samples',
      },
      {
        id: 'samples-summary',
        label: 'Samples Summary',
        icon: <TableIcon size={15} />,
        path: '/samples-summary',
        badge: 'Samples',
        badgeTooltip: 'This page is used for sample data manipulation',
      },
      {
        id: 'zones',
        label: 'Rock Types',
        icon: <LayersIcon size={15} />,
        path: '/zones',
      },
      {
        id: 'correlation',
        label: 'Correlation',
        icon: <LinkIcon size={15} />,
        path: '/correlation',
      },
      {
        id: 'kr-pc-model',
        label: 'Kr x Pc Model',
        icon: <GridIcon size={15} />,
        path: '/kr-pc-model',
      },
      {
        id: 'sw-height',
        label: (
          <span className={styles.itemLabelWithTag}>
            Sw-Height Calibration
            <span className={styles.betaTag}>beta</span>
          </span>
        ),
        icon: <TrendingDownIcon size={15} />,
        path: '/sw-height',
      },
    ],
  },
  {
    label: 'GCSReToolkit',
    items: [
      {
        id: 'ghe-inputs',
        label: 'ML vs Analogues',
        icon: <SlidersIcon size={15} />,
        path: '/ghe-inputs',
      },
      {
        id: 'analogue-tables',
        label: 'Analogue Tables',
        icon: <ListIcon size={15} />,
        path: '/analogue-tables',
      },
      {
        id: 'krel-pc-selection',
        label: 'Krel and Pc Analogue Selection',
        icon: <CheckSquareIcon size={15} />,
        path: '/krel-pc-selection',
      },
      {
        id: 'ix-tables',
        label: 'IX Tables',
        icon: <FileTextIcon size={15} />,
        path: '/ix-tables',
      },
    ],
  },
];

const PageSidebar = ({
  projectName,
  projectSubtitle,
  onBackToProjects,
  projects,
  onNewProject,
  onImportProject,
  onProjectSettings,
  sections = DEFAULT_SECTIONS,
  user,
  userAction,
  footer,
  className,
  ...rest
}: PageSidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const projectUid = searchParams.get('project');

  const handleNavigate = (path: string) => {
    const target = projectUid ? `${path}?project=${projectUid}` : path;
    navigate(target);
  };

  return (
    <Sidebar collapsible className={className} {...rest}>
      <Sidebar.Header className={styles.header}>
        <SidebarHeader
          title={projectName ?? 'Projects'}
          subtitle={projectSubtitle}
          projects={projects}
          onNewProject={onNewProject}
          onImport={onImportProject}
          onSettings={onProjectSettings}
        />
      </Sidebar.Header>

      <Sidebar.Nav>
        {sections.map(({ label, items }) => (
          <React.Fragment key={label}>
            <Sidebar.Separator label={label} />
            {items.map(({ id, label: itemLabel, icon, path, active: itemActive, onClick, disabled, badge, badgeTooltip }) => {
              const active = itemActive ?? (path ? location.pathname === path : false);
              const handleClick = onClick ?? (path ? () => handleNavigate(path) : undefined);

              return (
                <Sidebar.Item
                  key={id}
                  icon={icon}
                  active={active}
                  disabled={disabled}
                  badge={badge}
                  badgeTooltip={badgeTooltip}
                  onClick={handleClick}
                >
                  {itemLabel}
                </Sidebar.Item>
              );
            })}
          </React.Fragment>
        ))}
      </Sidebar.Nav>

      {(footer || user) && (
        <Sidebar.Footer className={styles.footer}>
          {footer || (user && <UserInfo {...user} action={userAction} />)}
        </Sidebar.Footer>
      )}
    </Sidebar>
  );
};

PageSidebar.displayName = 'PageSidebar';

export { PageSidebar };
